import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { Ledger, LedgerRestDataProvider } from '../../../data/LedgerRestDataProvider';
import { LedgerType, LedgerTypeLabels } from '../../../models/LedgerType';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const LedgerAccountsTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens', 'validation']);
    const [ledgers, setLedgers] = useState<Ledger[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const provider = React.useMemo(() => new LedgerRestDataProvider(), []);

    const columns: DetailedListColumn<Ledger>[] = [
        { key: 'code', header: t('screens:ledgerAccounts.columns.code'), dataField: 'code', type: 'string' },
        { key: 'name', header: t('screens:ledgerAccounts.columns.name'), dataField: 'name', type: 'string' },
        {
            key: 'type', header: t('screens:ledgerAccounts.columns.type'), dataField: 'type', type: 'select', options: [
                { label: 'Debet', value: LedgerType.Debet },
                { label: 'Credit', value: LedgerType.Credit },
                { label: 'Costs', value: LedgerType.Costs },
                { label: 'Proceeds', value: LedgerType.Proceeds }
            ],
            render: (l: Ledger) => {
                return LedgerTypeLabels[l.type];
            }
        },
        {
            //TODO: This needs to become dynamic
            key: 'group', header: t('screens:ledgerAccounts.columns.group'), dataField: 'group', type: 'select', options: [
                { label: 'Activa', value: 'Activa' },
                { label: 'Afschrijvingskosten', value: 'Afschrijvingskosten' },
                { label: 'Algemene Kosten', value: 'Algemene Kosten' },
                { label: 'Banken', value: 'Banken' },
                { label: 'BTW', value: 'BTW' },
                { label: 'Buitengewonen Lasten/Baten', value: 'Buitengewonen Lasten/Baten' },
                { label: 'Crediteuren', value: 'Crediteuren' },
                { label: 'Debiteuren', value: 'Debiteuren' },
                { label: 'Diverse Kosten', value: 'Diverse Kosten' },
                { label: 'Goederen', value: 'Goederen' },
                { label: 'Huisvestingskosten', value: 'Huisvestingskosten' },
                { label: 'Inspecties', value: 'Inspecties' },
                { label: 'Kantoorkosten', value: 'Kantoorkosten' },
                { label: 'Kassen', value: 'Kassen' },
                { label: 'Machinekosten', value: 'Machinekosten' },
                { label: 'Omzet', value: 'Omzet' },
                { label: 'Verkoop', value: 'Verkoop' },
                { label: 'Vermogen', value: 'Vermogen' },
                { label: 'Vooruit betaalde kosten', value: 'Vooruit betaalde kosten' },
                { label: 'Vrachtkosten', value: 'Vrachtkosten' },
            ]
        },
        { key: 'protected', header: t('screens:ledgerAccounts.columns.protected'), dataField: 'protected', type: 'checkbox', render: (l: Ledger) => { return l.protected ? "protected" : ""; } }
    ];

    useEffect(() => {
        setLoading(true);
        provider.getAll()
            .then(setLedgers)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [provider]);

    const handleChange = async (newLedgers: Ledger[], action: 'add' | 'edit' | 'delete', changed: Ledger) => {
        try {
            // Guard against mutating a ledger that was ALREADY protected before this edit.
            // We must check the original record, not the incoming `changed` data — because
            // `changed.protected` would be `true` when the user is in the process of
            // protecting a previously unprotected ledger, which should be allowed.
            const original = ledgers.find(l => l.id === changed.id);
            const wasProtected = original?.protected ?? false;

            if (action === 'add') {
                await provider.add(changed);
            } else if (action === 'edit' && !wasProtected) {
                await provider.update(changed.id, changed);
            } else if (action === 'delete' && !wasProtected) {
                await provider.delete(changed.id);
            } else if (wasProtected) {
                toast.error(t('validation:ledgerProtected'));
            }

            // Always refresh from API after mutation
            const refreshed = await provider.getAll();
            setLedgers(refreshed);
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div className="ledger-accounts-tab">
            <SubsectionPanel title={t('screens:ledgerAccounts.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <InfoPanel header="About these ledgers">
                    These are the ledgers which are part of your business administration.
                </InfoPanel>
                <DetailedListComponent
                    dataSource={{ data: ledgers, setData: setLedgers }}
                    columns={columns}
                    rowKey={r => r.id}
                    onChange={handleChange}
                />
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>
        </div>
    );
};

export default LedgerAccountsTab;